import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import axiosInstance from "../api/axiosConfig";
import { formatDisplayDateTime } from "../utils/formatters";
import { isDevSite, useDevSiteOverrides } from "../utils/devSites";
import { devSiteColumn, devSiteRowProps } from "../components/DevSiteSwitch";
import "./CancelledUser.css";

const CancelledUser = () => {
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDevSites, setShowDevSites] = useState(false);
  const navigate = useNavigate();
  const devSiteOverrides = useDevSiteOverrides();

  useEffect(() => {
    if (localStorage.getItem("isAuthenticated") !== "true") {
      navigate("/admin-login");
      return;
    }

    const fetchCancelledUsers = async () => {
      try {
        const response = await axiosInstance.get("/admin/installations");
        setInstallations(response.data.installations || []);
      } catch (err) {
        console.error("Error fetching cancelled paid users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCancelledUsers();
  }, [navigate]);

  const allRows = useMemo(
    () =>
      installations
        .filter(
          (item) =>
            item.app_installed === 0 &&
            item.plan &&
            item.plan !== "—" &&
            item.plan.toLowerCase() !== "free",
        )
        .map((item, index) => ({
          id: item.id ?? index,
          instanceId: item.instanceId || "—",
          installed_at: item.installed_at ?? null,
          reinstalled_at: item.reinstalled_at ?? null,
          uninstalled_at: item.uninstalled_at ?? null,
          app_installed: item.app_installed === 1 ? "Yes" : "No",
          plan: item.plan ?? "—",
          expires_on: item.expires_on ?? null,
          cycle: item.cycle ?? "—",
          url: item.url ?? "—",
          auto_renew:
            item.auto_renew === 1 ? "Yes" : item.auto_renew === 0 ? "No" : "—",
          cancelReason: item.cancelReason ?? "—",
          userReason: item.userReason ?? "—",
          subscriptionCancellationType:
            item.subscriptionCancellationType ?? "—",
          site_name: item.site_name ?? "—",
          plan_purchase_at: item.plan_purchase_at ?? null,
          is_dev_site: isDevSite(item, devSiteOverrides),
          raw: item,
        })),
    [installations, devSiteOverrides],
  );

  // Development sites never count as cancelled paid users, and stay hidden
  // unless the admin asks to see them (to un-mark one, for example).
  const countedRows = useMemo(
    () => allRows.filter((row) => !row.is_dev_site),
    [allRows],
  );
  const devRowCount = allRows.length - countedRows.length;
  const rows = showDevSites ? allRows : countedRows;

  const columns = [
    {
      accessorFn: (row, index) => index + 1,
      id: "serial",
      header: "ID",
      size: 70,
      Cell: ({ row }) => row.index + 1,
    },
    {
      accessorKey: "instanceId",
      header: "Instance ID",
      size: 140,
      muiTableBodyCellProps: {
        className: "mrt-cell-instanceId",
      },
    },
    {
      accessorFn: (row) =>
        row.installed_at ? new Date(row.installed_at) : null,
      id: "installed_at",
      header: "Installed At",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 172,
    },
    {
      accessorFn: (row) =>
        row.reinstalled_at ? new Date(row.reinstalled_at) : null,
      id: "reinstalled_at",
      header: "Reinstalled At",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 172,
    },
    {
      accessorFn: (row) =>
        row.uninstalled_at ? new Date(row.uninstalled_at) : null,
      id: "uninstalled_at",
      header: "Uninstalled At",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 172,
    },
    { accessorKey: "app_installed", header: "App Installed", size: 110 },
    devSiteColumn,
    { accessorKey: "plan", header: "Plan", size: 100 },
    {
      accessorFn: (row) => (row.expires_on ? new Date(row.expires_on) : null),
      id: "expires_on",
      header: "Expires On",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 172,
    },
    { accessorKey: "cycle", header: "Cycle", size: 100 },
    {
      accessorKey: "url",
      header: "URL",
      size: 150,
      muiTableBodyCellProps: {
        className: "mrt-cell-url",
      },
      Cell: ({ cell }) =>
        cell.getValue() !== "—" ? (
          <a
            href={cell.getValue()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="paid-users-link"
          >
            {cell.getValue()}
          </a>
        ) : (
          <span>—</span>
        ),
    },
    { accessorKey: "auto_renew", header: "Auto Renew", size: 100 },
    { accessorKey: "cancelReason", header: "Cancel Reason", size: 150 },
    { accessorKey: "userReason", header: "User Reason", size: 150 },
    {
      accessorKey: "subscriptionCancellationType",
      header: "Cancellation Type",
      size: 160,
    },
    { accessorKey: "site_name", header: "Site Name", size: 150 },
    {
      header: "Plan Purchased At",
      accessorFn: (row) =>
        row.plan_purchase_at ? new Date(row.plan_purchase_at) : null,
      id: "plan_purchase_at",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 172,
    },
  ];

  return (
    <Box className="cancelled-users-container">
      <Box className="cancelled-users-header">
        <Typography variant="h5" className="cancelled-users-title">
          Cancelled Paid Users ({countedRows.length})
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {devRowCount > 0 && (
            <FormControlLabel
              className="dev-site-filter"
              control={
                <Switch
                  size="small"
                  checked={showDevSites}
                  onChange={(e) => setShowDevSites(e.target.checked)}
                />
              }
              label={`Show dev sites (${devRowCount})`}
            />
          )}
          <Button
            variant="contained"
            className="cancelled-users-go-back-btn"
            onClick={() => navigate(-1)}
          >
            ⬅ Go Back
          </Button>
        </Box>
      </Box>

      <Box className="cancelled-users-table-wrapper">
        <MaterialReactTable
          columns={columns}
          data={rows}
          state={{ isLoading: loading }}
          enableColumnOrdering
          enablePagination
          enableSorting
          enableBottomToolbar
          enableColumnResizing
          columnResizeMode="onChange"
          muiTableContainerProps={{ sx: { height: "100%" } }}
          initialState={{
            pagination: { pageSize: 20 },
            sorting: [
              {
                id: "plan_purchase_at",
                desc: true,
              },
            ],
          }}
          muiTableBodyRowProps={({ row }) => devSiteRowProps(row)}
        />
      </Box>
    </Box>
  );
};

export default CancelledUser;
